<template>
    <div class="entity-editor">
        <div class="entity-editor__body">
            <div v-if="isLoading" class="entity-editor__body--loader-wrapper">
                <or-progress-circular
                    v-show="isLoading"
                    class="progress"
                    color="primary"
                    :size="30"
                />
            </div>
            <or-select
                v-if="isNewEntity"
                class="entity-type--selector"
                :label="$t('model.nlu-model.sidebar.entities.editors.entityeditor.type')"
                :placeholder="$t('model.nlu-model.sidebar.entities.editors.entityeditor.selectEntityType')"
                :value="localEntity.type"
                :options="entityTypesOptions"
                :disabled="isLoading || isDeleting"
                @change="updateEntityType"
            />

            <ListEntity
                v-if="showListEntity"
                :isLoading="isLoading || isDeleting"
                :entity.sync="$v.localEntity.$model"
                :errors="entityErrors"
            />

            <MachineLearningEntity
                v-else-if="showMachineLearningEntity"
                :isLoading="isLoading || isDeleting"
                :errors="entityErrors"
                :entity.sync="$v.localEntity.$model"
            />

            <PreBuiltEntity
                v-else-if="showPrebuiltEntity"
                :isNewEntity="isNewEntity"
                :selectedPrebuildEntities.sync="$v.localEntity.$model"
                :errors="entityErrors"
                :isLoading="isLoading || isDeleting"
                :isEntitySupported="isEntitySupported"
            />

            <RegexpEntity
                v-else-if="showRegExpEntity"
                :entity.sync="$v.localEntity.$model"
                :errors="entityErrors"
                :is-loading="isLoading || isDeleting"
            />

            <PatternAnyEntity
                v-else-if="showPatternAnyEntity"
                :entity.sync="$v.localEntity.$model"
                :errors="entityErrors"
                :is-loading="isLoading || isDeleting"
            />

            <!-- unsupported entity -->
            <or-textbox
                v-else
                v-model="localEntity.name"
                :label="$t('model.nlu-model.sidebar.entities.editors.entityeditor.name')"
                :placeholder="$t('model.nlu-model.sidebar.entities.editors.entityeditor.enterEntityName')"
                enforceMaxlength
                :readonly="isLoading"
                :invalid="false"
            />

            <or-alert
                v-show="errorText"
                type="error"
                @dismiss="errorText = ''"
            >
                {{errorText}}
            </or-alert>
        </div>

        <div class="entity-editor__footer">
            <or-button
                v-show="showDeleteBtn"
                class="delete-btn"
                type="secondary"
                color="red"
                :disabled="isLoading || isDeleting"
                :loading="isDeleting"
                @click="$refs.deleteEntityConfirm.open()"
            >
              {{ $t('generic.delete') }}
            </or-button>

            <or-button
                class="cancel-btn"
                color="primary"
                type="secondary"
                disabledRipple
                :disabled="isLoading || isDeleting"
                @click="$emit('cancel')"
            >
              {{ $t('generic.cancel') }}
            </or-button>

            <or-button
                color="primary"
                class="modal-btn"
                disabledRipple
                :disabled="confirmButtonIsDisabled || !isEntitySupported"
                :loading="isLoading"
                @click="onConfirm"
            >
                {{ !isNewEntity ? $t('generic.save') : $t('generic.create') }}
            </or-button>
        </div>

        <or-confirm
            ref="deleteEntityConfirm"
            :title="$t('model.nlu-model.sidebar.entities.editors.ml-entity.ml-entity-child.deleteEntityBtnTooltip')"
            @confirm="deleteEntityHandler"
        >
            {{deleteModalDescription}}
        </or-confirm>
    </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import { entityValidation } from '@/mixins/entities/entity-validation.mixin'

import PreBuiltEntity, { getDefaultPreBuildEntity } from './prebuilt-entity/PreBuiltEntity'
import ListEntity, { getDefaultListEntity } from './list-entity/ListEntity'
import MachineLearningEntity, { getDefaultMlEntity } from './ml-entity/ml-editor'
import RegexpEntity, { getDefaultRegexpEntity } from './regexp-entity/RegexpEntity'
import PatternAnyEntity, { getDefaultPatternAnyEntity } from './pattern-any-entity/PatternAnyEntity'

import { entityTypesOptions, ENTITY_TYPE } from '../entity-types'
import { getSupportedFeatures } from '@/config'

export default {
  components: {
    ListEntity,
    MachineLearningEntity,
    PreBuiltEntity,
    RegexpEntity,
    PatternAnyEntity
  },

  mixins: [entityValidation],

  props: {
    selectedEntity: Object,
    isNewEntity: Boolean
  },

  data () {
    return {
      localEntity: {
        id: undefined,
        ..._.cloneDeep(this.$props.selectedEntity)
      },
      isLoading: false,
      isDeleting: false,
      errorText: null,
      // Prebuild entity doesn't matter, because we simply enabling|disabling from list of prebuild entities
      entityTypesOptions
    }
  },

  computed: {
    ...mapState('knowledgebase', ['allEntities']),
    ...mapGetters('knowledgebase', ['getCurrent']),

    confirmButtonIsDisabled () {
      return this.$v.invalid || this.isLoading || this.isDeleting
    },

    showPrebuiltEntity () {
      return this.localEntity.type === ENTITY_TYPE.prebuilt
    },

    showListEntity () {
      return this.localEntity.type === ENTITY_TYPE.list
    },

    showMachineLearningEntity () {
      return this.localEntity.type === ENTITY_TYPE.machineLearning
    },

    showRegExpEntity () {
      return this.localEntity.type === ENTITY_TYPE.regexp
    },

    showPatternAnyEntity () {
      return this.localEntity.type === ENTITY_TYPE.patternAny
    },

    isEntitySupported () {
      return this.showPrebuiltEntity ? getSupportedFeatures(this.getCurrent.culture).prebuiltEntity : true
    },

    showDeleteBtn () {
      return !this.isNewEntity
    },

    deleteModalDescription () {
      if (this.showPatternAnyEntity) {
        return this.$t('model.nlu-model.sidebar.entities.editors.entityeditor.deleteModal.patternDescription')
      }
      return this.$t('model.nlu-model.sidebar.entities.editors.entityeditor.deleteModal.description')
    }
  },

  methods: {
    ...mapActions('knowledgebase', [
      'fetchCategories',
      'listAllModels',
      'updateEntity',
      'createEntity'
    ]),

    ...mapActions('knowledgebase/entities', [
      'createPrebuiltEntity',
      'updatePrebuiltEntity',
      'fetchAllEntities',
      'deleteEntity'
    ]),

    getPrebuildEntities (entitiesValue) {
      return entitiesValue
        .filter(entity => entity.type === ENTITY_TYPE.prebuilt)
        .map(entity => entity.name)
    },

    updateEntityType (entityType) {
      if (entityType === this.$props.selectedEntity.type) {
        this.localEntity = _.cloneDeep(this.$props.selectedEntity)
      } else {
        this.localEntity = this.getDefaultModel(entityType)
        this.$v.$reset()
      }
    },

    async deleteEntityHandler () {
      this.isDeleting = true
      await this.deleteEntity({ item: this.localEntity })
      this.isDeleting = false

      this.$emit('cancel')
    },

    getDefaultModel (entityType) {
      switch (entityType) {
        case ENTITY_TYPE.prebuilt: {
          return {
            ...getDefaultPreBuildEntity(),
            name: this.localEntity.name
          }
        }
        case ENTITY_TYPE.regexp: {
          return {
            ...getDefaultRegexpEntity(),
            name: this.localEntity.name
          }
        }
        case ENTITY_TYPE.list: {
          return {
            ...getDefaultListEntity(),
            name: this.localEntity.name
          }
        }
        case ENTITY_TYPE.machineLearning: {
          return {
            ...getDefaultMlEntity(),
            name: this.localEntity.name
          }
        }
        case ENTITY_TYPE.patternAny: {
          return {
            ...getDefaultPatternAnyEntity(),
            name: this.localEntity.name
          }
        }
        default:
          console.error('[getDefaultModel] Not supported entity type:', entityType)
          return this.localEntity
      }
    },

    async onConfirm () {
      this.$v.$touch()
      if (this.$v.$invalid) {
        console.debug('[onConfirm] Was not able to save:', this.$v.localEntity)
        return
      }

      this.isLoading = true
      this.$v.$reset()
      let updateFunction
      switch (this.localEntity.type) {
        case ENTITY_TYPE.prebuilt: {
          updateFunction = this.prebuiltEntityUpdate
          break
        }
        case ENTITY_TYPE.list:
        case ENTITY_TYPE.patternAny:
        case ENTITY_TYPE.machineLearning:
        case ENTITY_TYPE.regexp: {
          updateFunction = this.findEntityAndUpdate
          break
        }
        default: {
          console.error('[onConfirm] Not supported entity type:', this.localEntity)
          this.errorText = this.$t(
            'model.nlu-model.sidebar.entities.editors.entityeditor.errors.notSupportedEntityType',
            { entityType: this.localEntity.type }
          )
        }
      }

      if (updateFunction) {
        await updateFunction()
          .catch(err => {
            this.errorText = err.message
          })
      }

      this.isLoading = false

      if (!this.errorText) {
        this.$emit('cancel')
      }
    },

    async prebuiltEntityUpdate () {
      if (this.isNewEntity) {
        await this.createPrebuiltEntity(this.localEntity)
      } else {
        await this.updatePrebuiltEntity(this.localEntity)
      }
    },

    // Not suitable for prebuild entities
    async findEntityAndUpdate () {
      if (this.isNewEntity) {
        const { id } = await this.createEntity(this.localEntity)
        this.$v.localEntity.$model.id = id
      } else {
        await this.updateEntity(this.localEntity)
      }
    }
  }
}
</script>

<style lang="scss">
    .entity-type--helptext {
        margin-top: 0;
        color: rgb(145, 150, 157);
        font-size: 12px;
        font-weight: normal;
        line-height: 18px;
    }
</style>

<style lang="scss" scoped>

.entity-editor {
    position: relative;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    height: 100%;

    .entity-editor__body--loader-wrapper {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 3;
    }

    .entity-type--selector {
        margin-bottom: 0;
    }

    &__body {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        padding: $sp-4 $sp-3 0;
        max-height: calc(100% - 80px);
        overflow-y: scroll;
    }

    &__footer {
        display: flex;
        justify-content: flex-end;
        margin-top: $sp-3;
        padding: $sp-3;
        border-top: solid 1px $or-grey-medium;

        .cancel-btn {
            margin-right: $sp-3;
        }

        .delete-btn {
            margin-right: auto;
        }
    }
}
</style>
